import { alpha, Box, Button, FormHelperText, InputBase, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Shared_FormTemplate_QuestionTypeAmountFragment } from "../fragments/fragmentsTypes.generated";
import { NarrowedFormField } from "../types";

type Props = {
    disabled?: boolean;
    field: NarrowedFormField<Shared_FormTemplate_QuestionTypeAmountFragment>;
    value?: string | null;
    onChange?: (value: string) => void;
};

export default function QuestionAmount({ disabled, field, value, onChange }: Props) {
    const initialToggleValue = value?.endsWith(";estimated") ? "estimated" : "exact";
    const [amountModifier, setAmountModifier] = useState<ToggleOption>(initialToggleValue);
    const [isFocused, setIsFocused] = useState(false);

    // extracting the answer without the appended amount modifier first
    const rawValue = value?.replace(/;(exact|estimated)$/, "") ?? "";

    // if user changes the answer, re-append the modifer to the new value
    const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const combinedValue = e.target.value ? `${e.target.value};${amountModifier}` : "";
        onChange?.(combinedValue);
    };

    // if user also changes the toggle, append the new modifier to the raw value
    const handleToggleChange = (option: ToggleOption) => {
        setAmountModifier(option);
        if (onChange) {
            const value = rawValue ? `${rawValue};${option}` : "";
            onChange(value);
        }
    };

    return (
        <Stack rowGap={0.5}>
            <Box
                sx={theme => ({
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    padding: 1,
                    paddingInline: 1.5,
                    cursor: "text",
                    width: "100%",
                })}
            >
                <Typography
                    component="label"
                    variant="caption"
                    color={isFocused ? "primary" : "text.secondary"}
                    sx={theme => ({
                        fontSize: theme.typography.caption.fontSize,
                        lineHeight: 1.5,
                    })}
                >
                    {field.name}
                    {field.isRequired && " *"}
                </Typography>
                <Stack direction="row" justifyContent="space-between">
                    <InputBase
                        type="number"
                        value={rawValue}
                        onChange={handleAnswerChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        readOnly={disabled || !onChange}
                        placeholder={field.placeholder || undefined}
                        disabled={disabled}
                        sx={theme => ({
                            fontSize: theme.typography.body1.fontSize,
                            lineHeight: 1.5,
                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                display: "none",
                            },
                            "& input[type=number]": {
                                MozAppearance: "textfield",
                            },
                        })}
                    />
                    <AmountModifierToggle
                        selected={amountModifier}
                        onSelect={handleToggleChange}
                        disabled={disabled || !onChange}
                    />
                </Stack>
            </Box>
            {field.description && <FormHelperText sx={{ paddingInline: 1.5 }}>{field.description}</FormHelperText>}
        </Stack>
    );
}

const options = ["exact", "estimated"] as const;

type ToggleOption = (typeof options)[number];

type ToggleProps = {
    selected: ToggleOption;
    onSelect: (value: ToggleOption) => void;
    disabled: boolean;
};

function AmountModifierToggle({ selected, onSelect, disabled }: ToggleProps) {
    const { t } = useTranslation("shared");

    return (
        <Box paddingBlock={1}>
            <Box
                sx={theme => ({
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                    paddingInline: 1,
                    borderRadius: 1,
                    flexShrink: 0,
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 4,
                        bottom: 4,
                        left: selected === "exact" ? "8px" : "50%",
                        width: "calc(50% - 8px)",
                        backgroundColor: "primary.main",
                        borderRadius: 1,
                        transition: theme.transitions.create(["left", "width"], {
                            duration: theme.transitions.duration.standard,
                            easing: theme.transitions.easing.easeInOut,
                        }),
                    },
                })}
            >
                {options.map(option => (
                    <Button
                        key={option}
                        onClick={() => !disabled && onSelect(option)}
                        sx={{
                            paddingInline: 1,
                            flex: 1,
                            zIndex: 1,
                            textTransform: "none",
                            color: selected === option ? "white" : "text.primary",
                            whiteSpace: "nowrap",
                            cursor: disabled ? "default" : "pointer",
                        }}
                    >
                        {t(`QuestionAmount.${option}Btn`)}
                    </Button>
                ))}
            </Box>
        </Box>
    );
}
